/**
 * Outbound referrals (NEW-WF-12, module id `referrals`, routes under
 * `/v1/referrals`). Library: lab-referral-rx.md referrals LR-01..04,
 * patient-admin LR-01..04 and MM-08, imaging-docs MM-12.
 *
 * A referral holds the procedures it refers. Their status changes (Referred
 * on create, Existing Other on "mark done elsewhere") go through the chart's
 * write path, so each PMS's gates apply unchanged: where the PMS does not
 * take procedure edits from Velora the change is refused per procedure with
 * the reason, and the referral still records it.
 */
import { z } from 'zod';

const Id = z.uuid();
const Instant = z.string().min(1);
const LocalDate = z.iso.date();

export const REFERRALS_PERMISSIONS = {
  view: 'chart.view',
  /** Create, edit, send and close referrals (changes procedure status). */
  edit: 'chart.edit',
  /** Print the letter to Documents. */
  print: 'documents.upload',
  /** Maintain the specialist directory. */
  directory: 'settings.manage',
} as const;

export const SPECIALTIES = [
  'oral_surgery',
  'endodontics',
  'periodontics',
  'orthodontics',
  'pediatric',
  'prosthodontics',
  'oral_medicine',
  'other',
] as const;
export const SpecialtySchema = z.enum(SPECIALTIES);

export const AddressSchema = z.object({
  line1: z.string().trim().max(200).optional(),
  line2: z.string().trim().max(200).optional(),
  city: z.string().trim().max(100).optional(),
  state: z.string().trim().max(40).optional(),
  zip: z.string().trim().max(20).optional(),
});

export const SpecialistSchema = z.object({
  id: Id,
  name: z.string(),
  specialty: SpecialtySchema,
  practiceName: z.string().nullable(),
  phone: z.string().nullable(),
  fax: z.string().nullable(),
  email: z.string().nullable(),
  address: AddressSchema,
  active: z.boolean(),
});
export type Specialist = z.infer<typeof SpecialistSchema>;
export const SpecialistListSchema = z.object({
  specialists: z.array(SpecialistSchema),
  canManage: z.boolean(),
  /**
   * May add a specialist (anyone who makes referrals: chart.edit), inline
   * from a new referral (dentist role test, item 9). Editing and retiring
   * entries stays with canManage.
   */
  canAdd: z.boolean().optional(),
});
export const SpecialistBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  specialty: SpecialtySchema,
  practiceName: z.string().trim().max(200).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  fax: z.string().trim().max(40).nullable().optional(),
  email: z.string().trim().max(200).nullable().optional(),
  address: AddressSchema.optional(),
  active: z.boolean().optional(),
});

export const ReferralStatusSchema = z.enum(['draft', 'sent', 'scheduled', 'closed', 'cancelled']);
export const StatusChangeSchema = z.enum(['applied', 'queued', 'pms_blocked']);

export const ReferralProcedureSchema = z.object({
  procedureId: Id,
  code: z.string(),
  tooth: z.string().nullable(),
  description: z.string().nullable(),
  /** Current chart status (OD ProcStat: 1 TP, 4 EO, 5 R ...). */
  status: z.number().int(),
  statusBefore: z.number().int(),
  lastChange: StatusChangeSchema.nullable(),
  lastChangeDetail: z.string().nullable(),
});

export const ReferralSchema = z.object({
  id: Id,
  locationId: Id,
  patientId: Id,
  patientName: z.string().nullable(),
  specialist: SpecialistSchema,
  status: ReferralStatusSchema,
  reason: z.string().nullable(),
  urgency: z.enum(['routine', 'soon', 'urgent']),
  letterText: z.string().nullable(),
  letterDocumentId: Id.nullable(),
  sentAt: Instant.nullable(),
  sentByName: z.string().nullable(),
  followUpTaskId: Id.nullable(),
  followUpOn: LocalDate.nullable(),
  /** LR-03: sent 7 or more days ago and not scheduled: the row is amber. */
  followUpDue: z.boolean(),
  scheduledAt: Instant.nullable(),
  closedAt: Instant.nullable(),
  closedHow: z.enum(['done_elsewhere', 'cancelled']).nullable(),
  reportDocumentId: Id.nullable(),
  note: z.string().nullable(),
  procedures: z.array(ReferralProcedureSchema),
  /** Radiographs sent with the referral (dentist role test, item 9). */
  images: z
    .array(z.object({ id: Id, label: z.string(), takenOn: LocalDate.nullable() }))
    .optional(),
  createdAt: Instant,
});
export type Referral = z.infer<typeof ReferralSchema>;
export const ReferralListSchema = z.object({
  referrals: z.array(ReferralSchema),
  canEdit: z.boolean(),
  canPrint: z.boolean(),
});
export type ReferralList = z.infer<typeof ReferralListSchema>;

export const ReferralListQuerySchema = z.object({
  patientId: Id.optional(),
  status: z.enum(['open', 'all', 'draft', 'sent', 'scheduled', 'closed', 'cancelled']).optional(),
});

export const CreateReferralBodySchema = z.object({
  patientId: Id,
  specialistId: Id,
  procedureIds: z.array(Id).max(32).default([]),
  reason: z.string().trim().max(2000).nullable().optional(),
  urgency: z.enum(['routine', 'soon', 'urgent']).optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  /** Attach the patient's radiographs from the last 12 months (newest three studies). */
  attachXrays: z.boolean().optional(),
});
export const UpdateReferralBodySchema = z.object({
  specialistId: Id.optional(),
  reason: z.string().trim().max(2000).nullable().optional(),
  urgency: z.enum(['routine', 'soon', 'urgent']).optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  letterText: z.string().max(20000).nullable().optional(),
});
export const ReferralLetterBodySchema = z.object({
  /** The edited letter; omitted: the saved text, else the merged template. */
  text: z.string().min(1).max(20000).optional(),
});
export const ReferralLetterSchema = z.object({
  referral: ReferralSchema,
  documentId: Id,
});
export const DoneElsewhereBodySchema = z.object({
  /** The specialist's report already filed in Documents. */
  reportDocumentId: Id.nullable().optional(),
});
export const ScheduledBodySchema = z.object({ at: Instant.nullable().optional() });
export const CancelReferralBodySchema = z.object({ reason: z.string().trim().min(1).max(500) });
